import { Arg, Ctx, Directive, Mutation, Resolver } from 'type-graphql'
import { In } from 'typeorm'

import { createAttachment, deleteAttachmentsByRef } from '@things-factory/attachment-base'

import { DataKeySet } from './data-key-set.js'
import { DataKeySetPatch, NewDataKeySet } from './data-key-set-type.js'

@Resolver(DataKeySet)
export class DataKeySetMutation {
  @Directive('@transaction')
  @Mutation(returns => DataKeySet, {
    description:
      'Creates a new data key set for organizing and categorizing data samples. This defines the indexing structure used for data partitioning and search.'
  })
  async createDataKeySet(
    @Arg('dataKeySet') dataKeySet: NewDataKeySet,
    @Ctx() context: ResolverContext
  ): Promise<DataKeySet> {
    const { domain, user, tx } = context.state

    const result = await tx.getRepository(DataKeySet).save({
      ...dataKeySet,
      domain,
      creator: user,
      updater: user
    })

    if (dataKeySet.thumbnail) {
      await createAttachment(
        null,
        {
          attachment: {
            file: dataKeySet.thumbnail,
            refType: DataKeySet.name,
            refBy: result.id
          }
        },
        context
      )
    }

    return result
  }

  @Directive('@transaction')
  @Mutation(returns => DataKeySet, {
    description:
      'Updates a data key set configuration including key items and thumbnail. This mutation maintains the indexing structure for data organization.'
  })
  async updateDataKeySet(
    @Arg('id') id: string,
    @Arg('patch') patch: DataKeySetPatch,
    @Ctx() context: ResolverContext
  ): Promise<DataKeySet> {
    const { domain, user, tx } = context.state

    const repository = tx.getRepository(DataKeySet)
    const dataKeySet = await repository.findOne({
      where: { domain: { id: domain.id }, id }
    })

    const result = await repository.save({
      ...dataKeySet,
      ...patch,
      updater: user
    })

    if (patch.thumbnail) {
      await deleteAttachmentsByRef(null, { refBys: [result.id] }, context)
      await createAttachment(
        null,
        {
          attachment: {
            file: patch.thumbnail,
            refType: DataKeySet.name,
            refBy: result.id
          }
        },
        context
      )
    }

    return result
  }

  @Directive('@transaction')
  @Mutation(returns => [DataKeySet], {
    description:
      'Updates multiple data key sets in a single operation. This mutation supports batch processing for efficient management of data categorization structures.'
  })
  async updateMultipleDataKeySet(
    @Arg('patches', type => [DataKeySetPatch]) patches: DataKeySetPatch[],
    @Ctx() context: ResolverContext
  ): Promise<DataKeySet[]> {
    const { domain, user, tx } = context.state

    let results = []
    const _createRecords = patches.filter((patch: any) => patch.cuFlag.toUpperCase() === '+')
    const _updateRecords = patches.filter((patch: any) => patch.cuFlag.toUpperCase() === 'M')
    const dataKeySetRepo = tx.getRepository(DataKeySet)

    if (_createRecords.length > 0) {
      for (let i = 0; i < _createRecords.length; i++) {
        const newRecord = _createRecords[i]

        const result = await dataKeySetRepo.save({
          ...newRecord,
          domain,
          creator: user,
          updater: user
        })

        if (newRecord.thumbnail) {
          await createAttachment(
            null,
            {
              attachment: {
                file: newRecord.thumbnail,
                refType: DataKeySet.name,
                refBy: result.id
              }
            },
            context
          )
        }

        results.push({ ...result, cuFlag: '+' })
      }
    }

    if (_updateRecords.length > 0) {
      for (let i = 0; i < _updateRecords.length; i++) {
        const updateRecord = _updateRecords[i]
        const dataKeySet = await dataKeySetRepo.findOneBy({ id: updateRecord.id })

        const result = await dataKeySetRepo.save({
          ...dataKeySet,
          ...updateRecord,
          updater: user
        })

        if (updateRecord.thumbnail) {
          await deleteAttachmentsByRef(null, { refBys: [result.id] }, context)
          await createAttachment(
            null,
            {
              attachment: {
                file: updateRecord.thumbnail,
                refType: DataKeySet.name,
                refBy: result.id
              }
            },
            context
          )
        }

        results.push({ ...result, cuFlag: 'M' })
      }
    }

    return results
  }

  @Directive('@transaction')
  @Mutation(returns => Boolean, {
    description:
      'Deletes a data key set and its associated thumbnail. This operation removes the categorization structure and all related attachments.'
  })
  async deleteDataKeySet(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<boolean> {
    const { domain, tx } = context.state

    await tx.getRepository(DataKeySet).delete({ domain: { id: domain.id }, id })
    await deleteAttachmentsByRef(null, { refBys: [id] }, context)

    return true
  }

  @Directive('@transaction')
  @Mutation(returns => Boolean, {
    description:
      'Deletes multiple data key sets in a single operation. This mutation supports batch deletion for efficient cleanup of categorization structures.'
  })
  async deleteDataKeySets(
    @Arg('ids', type => [String]) ids: string[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, tx } = context.state

    await tx.getRepository(DataKeySet).delete({
      domain: { id: domain.id },
      id: In(ids)
    })

    await deleteAttachmentsByRef(null, { refBys: ids }, context)

    return true
  }

  @Directive('@transaction')
  @Mutation(returns => Boolean, {
    description:
      'Imports multiple data key sets from external sources. This mutation supports bulk import of data categorization structures.'
  })
  async importDataKeySets(
    @Arg('dataKeySets', type => [DataKeySetPatch]) dataKeySets: DataKeySetPatch[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, tx } = context.state

    await Promise.all(
      dataKeySets.map(async (dataKeySet: DataKeySetPatch) => {
        const createdDataKeySet: DataKeySet = await tx.getRepository(DataKeySet).save({ domain, ...dataKeySet })
      })
    )

    return true
  }
}
